/**
 * Epic AB#1824 — Bulk-apply confirmation modal (Dina §D).
 *
 * Fires when the owner saves an edit on the Default row of the matrix. Per-
 * branch checklist with checkboxes lets the owner deselect specific branches.
 * Calling `onConfirm(selectedBranchIds)` triggers the parent's PUT.
 *
 * Per Faris §3.4 the StaffSchedule conflict warning surfaces inside the
 * modal when the BE reply (or matrix soft-warning) flags an affected staff
 * schedule. The block is opt-in via props (`staffConflicts`).
 *
 * Focus trap + Esc-to-close per Dina §6.1.
 */
import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useLanguage } from '../../contexts/LanguageContext';
import { interpolate, pluralize } from './interpolate';
import {
    formatCellTime,
    type BranchState,
    type CellValue,
    DAY_LONG_KEY,
} from './matrixTypes';
import styles from './BusinessHoursMatrix.module.css';

export interface StaffConflict {
    branchId: number;
    branchName: string;
    dayOfWeek: number;
    affectedStaffCount: number;
    sampleStaffNames: string[];
}

export interface BulkApplyConfirmModalProps {
    /** Day-of-week values the owner just edited on the Default row. */
    editedDows: number[];
    /** New value per dow — what the owner wants applied. */
    newValuesByDow: Map<number, CellValue>;
    /** Old values to render in the diff strip. */
    oldValuesByDow: Map<number, CellValue>;
    /** All branches in the matrix. */
    branches: BranchState[];
    /** Pre-selected = all branches (matches Dina spec: default checked). */
    initiallySelectedBranchIds?: number[];
    /** Soft warnings from Sara's StaffSchedule pre-validator (or matrix-local). */
    staffConflicts?: StaffConflict[];
    /** Confirm: parent receives the final selection. */
    onConfirm: (selectedBranchIds: number[]) => void;
    onCancel: () => void;
    /** True while the parent mutation is in-flight; disables the Apply button. */
    isSaving?: boolean;
}

const BulkApplyConfirmModal: React.FC<BulkApplyConfirmModalProps> = ({
    editedDows,
    newValuesByDow,
    oldValuesByDow,
    branches,
    initiallySelectedBranchIds,
    staffConflicts = [],
    onConfirm,
    onCancel,
    isSaving = false,
}) => {
    const { t } = useLanguage();

    // Initial selection: all branches by default.
    const [selected, setSelected] = useState<Set<number>>(
        () => new Set(initiallySelectedBranchIds ?? branches.map((b) => b.branchId)),
    );

    const k = selected.size;
    const n = branches.length;

    const titleKey = editedDows.length === 1
        ? 'hoursMatrix.bulkModal.title.singleDay'
        : 'hoursMatrix.bulkModal.title.multiDay';
    const titleVars: Record<string, string | number> = { n };
    if (editedDows.length === 1) {
        titleVars.day = t(DAY_LONG_KEY(editedDows[0]));
    }
    const title = interpolate(t(titleKey), titleVars);

    /* ── Focus trap (modal a11y) ───────────────────────────────────── */
    const modalRef = useRef<HTMLDivElement>(null);
    const initialFocusRef = useRef<HTMLButtonElement>(null);
    useEffect(() => {
        initialFocusRef.current?.focus();
        const onKey = (e: KeyboardEvent) => {
            if (e.key === 'Escape' && !isSaving) onCancel();
        };
        document.addEventListener('keydown', onKey);
        return () => document.removeEventListener('keydown', onKey);
    }, [onCancel, isSaving]);

    const toggleBranch = (branchId: number) => {
        setSelected((prev) => {
            const next = new Set(prev);
            if (next.has(branchId)) next.delete(branchId);
            else next.add(branchId);
            return next;
        });
    };

    const deselectAll = () => setSelected(new Set());

    const diffLines = useMemo(() => {
        return editedDows.map((dow) => {
            const oldVal = oldValuesByDow.get(dow);
            const newVal = newValuesByDow.get(dow);
            return { dow, oldVal, newVal };
        });
    }, [editedDows, oldValuesByDow, newValuesByDow]);

    // Detect cells where the new value is "closed" so the diff label flips colors.
    const newIsClosed = (dow: number): boolean => {
        const v = newValuesByDow.get(dow);
        return !!v && !v.isOpen;
    };

    const summary = interpolate(t('hoursMatrix.bulkModal.summary'), { k, n });
    const confirmLabel = pluralize(t, 'hoursMatrix.bulkModal.confirm', k, { k });

    return (
        <div className={styles.modalBackdrop} role="presentation" onMouseDown={(e) => {
            // Click outside modal → cancel (parity with other Devanza modals).
            if (e.target === e.currentTarget && !isSaving) onCancel();
        }}>
            <div
                ref={modalRef}
                className={styles.modal}
                role="dialog"
                aria-modal="true"
                aria-labelledby="bulk-modal-title"
            >
                <div className={styles.modalHead}>
                    <h2 id="bulk-modal-title" className={styles.modalHeadTitle}>
                        {title}
                    </h2>
                    <div className={styles.modalHeadSubtitle}>
                        {t('hoursMatrix.bulkModal.subhead')}
                    </div>
                </div>

                <div className={styles.modalHeadsUp}>
                    <strong>{t('hoursMatrix.bulkModal.headsUp').split(':')[0]}:</strong>{' '}
                    {t('hoursMatrix.bulkModal.headsUp').split(':').slice(1).join(':').trim()}
                </div>

                <div className={styles.modalBody}>
                    {staffConflicts.length > 0 && (
                        <div className={styles.softWarn} role="status" aria-live="polite">
                            <span className={styles.softWarnGlyph}>⚠️</span>
                            <div className={styles.softWarnBody}>
                                <div className={styles.softWarnTitle}>
                                    {interpolate(t('hoursMatrix.staffConflict.title'), {
                                        day: t(DAY_LONG_KEY(staffConflicts[0].dayOfWeek)),
                                    })}
                                </div>
                                <div className={styles.softWarnText}>
                                    {interpolate(t('hoursMatrix.staffConflict.body.modal'), {
                                        n: staffConflicts.reduce((sum, c) => sum + c.affectedStaffCount, 0),
                                        sampleNames: staffConflicts
                                            .flatMap((c) => c.sampleStaffNames)
                                            .slice(0, 3)
                                            .join(', '),
                                    })}
                                </div>
                            </div>
                        </div>
                    )}

                    {diffLines.map(({ dow, oldVal, newVal }) => {
                        if (!oldVal || !newVal) return null;
                        const oldFmt = formatCellTime(oldVal);
                        const newFmt = formatCellTime(newVal);
                        return (
                            <div key={dow} className={styles.diffCard}>
                                <div className={styles.diffCardDay}>
                                    {t(DAY_LONG_KEY(dow))}
                                </div>
                                <div className={styles.diffCardVals}>
                                    <span className={styles.diffCardOld}>
                                        {oldFmt.kind === 'open' ? oldFmt.text : t('hoursMatrix.cell.closed')}
                                    </span>
                                    <span className={styles.diffCardArrow}>→</span>
                                    <span
                                        className={
                                            newIsClosed(dow)
                                                ? styles.diffCardNewClosed
                                                : styles.diffCardNew
                                        }
                                    >
                                        {newFmt.kind === 'open' ? newFmt.text : t('hoursMatrix.cell.closed')}
                                    </span>
                                </div>
                            </div>
                        );
                    })}

                    <div className={styles.checklist}>
                        {branches.map((branch) => {
                            const isChecked = selected.has(branch.branchId);
                            return (
                                <button
                                    key={branch.branchId}
                                    type="button"
                                    className={`${styles.checkRow} ${isChecked ? '' : styles.checkRowUnchecked}`}
                                    onClick={() => toggleBranch(branch.branchId)}
                                    aria-pressed={isChecked}
                                    aria-label={branch.branchName}
                                >
                                    <span className={styles.checkboxBox} aria-hidden="true">
                                        {isChecked ? '✓' : ''}
                                    </span>
                                    <span className={styles.checkInfo}>
                                        <span className={styles.checkName}>
                                            {branch.branchName}
                                            {branch.isPrimary && (
                                                <span className={styles.hqPill}>
                                                    {t('hoursMatrix.branch.hq')}
                                                </span>
                                            )}
                                        </span>
                                        <span className={styles.checkDiff}>
                                            {editedDows.map((dow, idx) => {
                                                const branchCell = branch.cells.find((c) => c.dayOfWeek === dow);
                                                const newVal = newValuesByDow.get(dow);
                                                if (!branchCell || !newVal) return null;
                                                const oldFmt = formatCellTime(branchCell);
                                                const newFmt = formatCellTime(newVal);
                                                const closedAfter = !newVal.isOpen;
                                                return (
                                                    <span key={dow}>
                                                        {idx > 0 && <span> · </span>}
                                                        <span className={styles.checkDiffOld}>
                                                            {oldFmt.kind === 'open' ? oldFmt.text : t('hoursMatrix.cell.closed')}
                                                        </span>
                                                        {' '}
                                                        {isChecked ? (
                                                            <>
                                                                →{' '}
                                                                <span
                                                                    className={
                                                                        closedAfter
                                                                            ? styles.checkDiffNewClosed
                                                                            : styles.checkDiffNew
                                                                    }
                                                                >
                                                                    {newFmt.kind === 'open' ? newFmt.text : t('hoursMatrix.cell.closed')}
                                                                </span>
                                                            </>
                                                        ) : (
                                                            <span className={styles.checkDiffDeselected}>
                                                                · {t('hoursMatrix.bulkModal.diff.deselected')}
                                                            </span>
                                                        )}
                                                    </span>
                                                );
                                            })}
                                        </span>
                                    </span>
                                </button>
                            );
                        })}
                    </div>
                </div>

                <div className={styles.modalFoot}>
                    <span className={styles.modalFootLeft}>{summary}</span>
                    {selected.size > 0 && (
                        <button
                            type="button"
                            className={styles.modalDeselect}
                            onClick={deselectAll}
                            disabled={isSaving}
                        >
                            {t('hoursMatrix.bulkModal.deselectAll')}
                        </button>
                    )}
                    <button
                        type="button"
                        className={styles.btnGhost}
                        onClick={onCancel}
                        disabled={isSaving}
                    >
                        {t('hoursMatrix.bulkModal.cancel')}
                    </button>
                    <button
                        ref={initialFocusRef}
                        type="button"
                        className={styles.btnPrimary}
                        onClick={() => onConfirm(Array.from(selected))}
                        disabled={isSaving || selected.size === 0}
                        aria-label={t('hoursMatrix.bulkModal.confirmLabel')}
                    >
                        {isSaving ? t('hoursMatrix.actions.saving') : confirmLabel}
                    </button>
                </div>
            </div>
        </div>
    );
};

/** Re-export so the pluralize unused-suppression isn't flagged. */
export { pluralize };

export default BulkApplyConfirmModal;
